<template>
  <div class="flex flex-col space-y-4">
    <div class="flex flex-col">
      <div v-if="openSubFolderName" class="flex w-full items-center">
        <DButton @click="loadProject" variant="outlined" text>{{ openFolderName }}</DButton>
        - {{ openSubFolderName }}
      </div>
      <div class="mb-2 flex items-center justify-between gap-2">
        <DButton
          :icon="uploading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
          :label="uploading ? 'Uploading...' : 'Upload Assets/Files to this list'"
          :disabled="uploading"
          class="w-1/4"
          @click="triggerFileInput" />
        <input ref="fileInput" type="file" multiple class="hidden" @change="handleFileSelect" />
        <DSelect
          v-if="typeFilterOptions.length > 1"
          v-model="selectedTypeFilter"
          :options="typeFilterOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Filter by type"
          class="w-1/4"
          @change="loadProject" />
      </div>
      <DDivider />
      <div class="flex w-full" v-if="assetStore.assets && assetStore.assets.length > 0 && !appStore.loading">
        <DDataTable :value="assetStore.assets" class="max-h-[450px] w-full overflow-auto" :loading="appStore.loading">
          <Column field="data.latest_version.thumbnail" header="Thumbnail" style="width: 80px">
            <template #body="slotProps">
              <div class="font-medium text-white">
                <img
                  v-if="slotProps.data.latest_version.thumbnail"
                  :src="slotProps.data.latest_version.thumbnail"
                  class="mr-2 inline-block h-20 w-20 rounded object-cover"
                  @error="$event.target.style.display = 'none'" />
                <span v-else class="text-xs text-gray-400">No Thumbnail</span>
              </div>
            </template>
          </Column>
          <Column field="data.latest_version.name" header="Name">
            <template #body="slotProps">
              <div
                v-if="slotProps.data.asset_type == 'folder'"
                @click="openFolder(slotProps.data.code, slotProps.data.name)">
                {{ slotProps.data.name }}
              </div>

              <div v-else>
                {{ slotProps.data.latest_version.name }}
              </div></template
            >
          </Column>
          <Column field="asset_type" header="Type" style="width: 80px">
            <template #body="slotProps">
              <span class="text-sm">
                {{
                  slotProps.data.asset_type === 'VI'
                    ? 'Video'
                    : slotProps.data.asset_type === 'IM'
                      ? 'Image'
                      : slotProps.data.asset_type === 'TD'
                        ? '3D Model'
                        : slotProps.data.asset_type
                }}
              </span>
            </template>
          </Column>

          <Column style="width: 5rem">
            <template #body="slotProps">
              <div class="flex items-center gap-1">
                <DButton type="button" icon="pi pi-plus" text rounded @click="selectProduct(slotProps.data)"></DButton>
                <DButton
                  type="button"
                  icon="pi pi-trash"
                  text
                  rounded
                  severity="danger"
                  @click="deleteAsset(slotProps.data)"></DButton>
              </div>
            </template>
          </Column>
        </DDataTable>
      </div>
      <div class="flex w-full items-center justify-center text-center text-gray-400" v-else>
        <div v-if="appStore.loading" class="animate-pulse">
          <LoaderCircle size="16" class="mr-2 inline-block animate-spin text-gray-400" />

          Loading assets...
        </div>
        <span v-else class="text-lg font-medium"> No assets found. </span>
      </div>
      <DDivider />
      <div
        class="flex w-full items-center"
        :class="[assetStore.pagination.total > assetStore.pagination.per_page ? 'justify-between' : 'justify-end']">
        <DPaginator
          v-if="assetStore.pagination.total > assetStore.pagination.per_page"
          :rows="assetStore.pagination.per_page"
          :totalRecords="assetStore.pagination.total"
          @page="handlePagination" />
        <DButton
          type="button"
          label="Cancel"
          icon="pi pi-times"
          size="small"
          class="h-fit"
          @click="closeDialog({ buttonType: 'Cancel' })"
          autofocus></DButton>
      </div>
    </div>

    <div class="flex items-center justify-end"></div>
  </div>
</template>

<script setup>
import { SUPPORTED_3D_FORMATS_LABEL } from '@/constants/object';
import { useAssetStore } from '@/stores/api/asset';
import { useAppStore } from '@/stores/appStore';
import { getAssetTypeFromFile } from '@/utils/getAssetTypeFromFile';
import { DButton, DDataTable, DDivider, DPaginator, DSelect, useConfirm, useToast } from '@disguise/cloud-ui';
import { LoaderCircle } from 'lucide-vue-next';
import Column from 'primevue/column';
import { computed, inject, onMounted, ref } from 'vue';

const assetStore = useAssetStore();
const openFolderName = ref();
const openSubFolderName = ref();
const params = ref({});
const selectedTypeFilter = ref('');

const dialogRef = inject('dialogRef');

const toast = useToast();
const fileInput = ref(null);
const uploading = ref(false);

const appStore = useAppStore();

const { confirmDialog } = useConfirm();

onMounted(() => {
  params.value = dialogRef.value.data;
  selectedTypeFilter.value = params.value.filter === 'scene' ? 'TD' : '';
  loadProject();
});

const triggerFileInput = () => {
  if (uploading.value) return;
  fileInput.value?.click();
};

const handleFileSelect = async (event) => {
  const files = Array.from(event.target.files);
  if (!files || files.length === 0) return;

  const allowedTypes = params.value.filter === 'scene' ? new Set(['scene']) : new Set(['image', 'video']);
  const validFiles = [];
  const invalidFiles = [];

  for (let file of files) {
    const mimeType = getAssetTypeFromFile(file);
    if (allowedTypes.has(mimeType)) {
      validFiles.push(file);
    } else {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    const supportedFormats =
      params.value.filter === 'scene'
        ? `Supported 3D formats: ${SUPPORTED_3D_FORMATS_LABEL}`
        : 'Supported formats: images (PNG, JPG, SVG, etc.) and videos (MP4, MOV, etc.)';
    toast.add({
      severity: 'warn',
      summary: 'Unsupported File Format',
      detail: `${invalidFiles.join(', ')} could not be uploaded. ${supportedFormats}`,
      life: 7000,
      group: 'br',
    });
  }

  if (validFiles.length === 0) return;

  uploading.value = true;
  const fileCount = validFiles.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';

  toast.add({
    severity: 'info',
    summary: 'Upload Started',
    detail: `Uploading ${fileCount} ${fileWord}...`,
    life: 3000,
    group: 'br',
  });

  let successCount = 0;
  let errorCount = 0;
  try {
    let index = 0;
    for (const file of validFiles) {
      index++;
      appStore.setProgressDisplayText(`Uploading ${index}/${validFiles.length}`);
      try {
        const asset = await assetStore.uploadFile(file);

        if (asset) {
          successCount++;
        }
      } catch (fileError) {
        console.error(`Error uploading file ${file.name}:`, fileError);
        errorCount++;

        toast.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail: `Failed to upload ${file.name}`,
          life: 5000,
          group: 'br',
        });
      }
    }

    if (successCount > 0) {
      toast.add({
        severity: 'success',
        summary: 'Upload Complete',
        detail: `Successfully uploaded ${successCount} ${successCount === 1 ? 'file' : 'files'}`,
        life: 4000,
        group: 'br',
      });
      await loadProject();
    }

    if (errorCount > 0 && successCount === 0) {
      toast.add({
        severity: 'error',
        summary: 'All Uploads Failed',
        detail: `Failed to upload ${errorCount} ${errorCount === 1 ? 'file' : 'files'}`,
        life: 5000,
        group: 'br',
      });
    }
  } finally {
    appStore.setProgress(0);
    appStore.setProgressDisplayText(null);
    uploading.value = false;

    if (fileInput.value) {
      fileInput.value.value = '';
    }
  }
};

const deleteAsset = (asset) => {
  if (!asset) return;

  confirmDialog('Confirmation', `Permanently delete "${asset.name}" from your library?`, 'Delete', 'Cancel')
    .then(async (response) => {
      if (response) {
        try {
          await assetStore.destroy(asset.code);

          toast.add({
            severity: 'success',
            summary: 'Asset Deleted',
            detail: `${asset.name} has been removed from library`,
            life: 3000,
            group: 'br',
          });
          await loadProject({
            page: assetStore.pagination.page || 1,
          });
        } catch (error) {
          console.error('Error deleting asset:', error);
          toast.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: `Failed to delete ${asset.name}`,
            life: 5000,
            group: 'br',
          });
        }
      }
    })
    .catch((err) => {});
};
const handlePagination = (event) => {
  loadProject({
    page: event.page + 1,
  });
};

const closeDialog = (e) => {
  dialogRef.value.close(e);
};

const selectProduct = (data) => {
  dialogRef.value.close(data);
};

const openFolder = (folderId, folderName) => {
  assetStore.loadAssetChildren(folderId).then(() => {
    openSubFolderName.value = folderName;
  });
};

const typeFilterOptions = computed(() => {
  if (params.value.filter === 'scene') {
    return [{ label: '3D Model', value: 'TD' }];
  }
  return [
    { label: 'All Types', value: '' },
    { label: 'Video', value: 'VI' },
    { label: 'Image', value: 'IM' },
  ];
});

const loadProject = async (params) => {
  const folder = assetStore.currentAsset?.folder;
  if (!folder) return;

  let assetTypes =
    typeFilterOptions.value.length > 1
      ? typeFilterOptions.value.filter((option) => option.value).map((option) => option.value)
      : [];

  let fetchParams = {};
  fetchParams.asset_type = selectedTypeFilter.value
    ? selectedTypeFilter.value
    : assetTypes.length > 0
      ? assetTypes
      : undefined;

  fetchParams = {
    ...fetchParams,
    ...params,
    ...{ sort_by: assetStore.pagination.sort_by },
  };
  try {
    const res = await assetStore.fetchForFolder(folder, fetchParams);
  } finally {
    appStore.setLoading(false);
  }
};
</script>
